import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError } from '../api';
import { groupBookingEndpoints } from '../api-group-bookings';
import type { Board } from '../api-group-bookings';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { ErrorNote, Panel } from '../ui';
import { INPUT } from './event/shared';

/**
 * The board (D-376): every game down the side, every space across the top,
 * and in each cell whether it is open, held or booked, and by whom. The one
 * screen that answers "is the suite free on the 14th" without opening anything.
 */
export default function GroupBoardPage() {
  const can = usePermissions();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const board = useQuery<Board, ApiError>({ queryKey: ['group-board', from, to], queryFn: () => groupBookingEndpoints.board({ ...(from ? { from } : {}), ...(to ? { to } : {}) }) });
  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;

  const products = board.data?.products ?? [];
  return (
    <div className="space-y-4">
      <Panel
        title="Spaces by game"
        description="Open, held (until a day) or booked, per game. Click a name to open its lead."
        actions={
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="board-from">From</label>
            <input id="board-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
            <label htmlFor="board-to">to</label>
            <input id="board-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
          </div>
        }
      >
        {board.error ? <ErrorNote>{board.error.message}</ErrorNote> : null}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Game</th>
                {products.map((p) => (
                  <th key={p.id} className="px-3 py-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(board.data?.games ?? []).map((game) => (
                <tr key={game.performance_id} className="border-b border-line last:border-0 align-top">
                  <td className="px-4 py-2">
                    <div className="font-medium">{game.local_event_date}</div>
                    <div className="text-xs text-ink-500">{game.event_title}</div>
                  </td>
                  {products.map((p) => {
                    const cell = game.spaces.find((s) => s.product_id === p.id);
                    if (!cell) return <td key={p.id} className="px-3 py-2 text-ink-400">—</td>;
                    const tone = cell.booked >= cell.per_game_limit ? 'bg-ok-soft' : cell.held + cell.booked >= cell.per_game_limit ? 'bg-warn-soft' : '';
                    return (
                      <td key={p.id} className={`px-3 py-2 ${tone}`}>
                        {(cell.bookings ?? []).map((b) => (
                          <div key={b.id} className="text-xs">
                            {b.lead_id ? (
                              <Link to={`/group-sales/leads/${b.lead_id}`} className="text-live hover:underline">
                                {b.name}
                              </Link>
                            ) : (
                              b.name
                            )}{' '}
                            <span className="text-ink-500">({b.status.toLowerCase()})</span>
                          </div>
                        ))}
                        {cell.open > 0 ? <div className="text-xs text-ink-500">{cell.per_game_limit > 1 ? `${cell.open} open` : 'Open'}</div> : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
